import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { REGULAR_EXPRESSION_NEW } from 'src/constants/common';
import { INotificationTypes } from 'src/services/CustomerService';

/**
 * Per-load Send to for order notifications (cp-meeting-changes F2). The user saving an order's
 * notification settings can send that load's emails and texts to other addresses; the backend
 * stores them in order_metadata.notificationTypes and WALHQ sends there for that user only.
 */

/** The stored order config, as the order detail and Track endpoints return it. */
export interface IOrderNotificationConfig {
    notificationTypes?: INotificationTypes | null;
    configuredByContactsCustomersId?: number | null;
    deliveryEmail?: string | null;
    deliveryPhone?: string | null;
}

export interface IDeliveryAddresses {
    email: string;
    phone: string;
    /** The user's own profile values (normalized), so a field left at them is not stored as an override. */
    profileEmail: string;
    profilePhone: string;
}

export interface IDeliveryPayload {
    deliveryEmail: string | null;
    deliveryPhone: string | null;
}

interface IDeliveryUser {
    id?: number | null;
    email?: string | null;
    phone?: string | null;
}

/** E.164 for a valid number (US/CA default region, same rule as the Share Tracking Link modal), else null. */
export const normalizeDeliveryPhone = (raw?: string | null): string | null => {
    const trimmed = raw?.trim();
    if (!trimmed) return null;

    const parsed = parsePhoneNumberFromString(trimmed, 'US');

    return parsed?.isValid() ? parsed.number : null;
};

/**
 * What the Send to fields start with: the addresses this user saved on this load, else the user's
 * profile. A config saved by another user never prefills, because saving makes this user the
 * owner of the order's settings and their messages would go to someone else's address.
 */
export const getDeliveryDefaults = (orderConfig: IOrderNotificationConfig | null | undefined, currentUser: IDeliveryUser | null | undefined): IDeliveryAddresses => {
    const isOwnConfig = !!orderConfig && !!currentUser?.id && orderConfig.configuredByContactsCustomersId === currentUser.id;
    const profileEmail = currentUser?.email?.trim().toLowerCase() || '';
    const profilePhone = normalizeDeliveryPhone(currentUser?.phone) || '';

    return {
        email: (isOwnConfig && orderConfig?.deliveryEmail) || profileEmail,
        phone: (isOwnConfig && orderConfig?.deliveryPhone) || profilePhone,
        profileEmail,
        profilePhone,
    };
};

/**
 * What Save sends. A shown field that is empty or invalid is an error (red border, nothing saved).
 * A hidden field, or one left at the user's profile value, goes out as null: WALHQ then uses the
 * profile at send time, so a later profile change still reaches this load (storing the prefilled
 * profile value would freeze it on the order).
 */
export const buildDeliveryPayload = (
    fields: { email: string; phone: string; showEmail: boolean; showPhone: boolean },
    delivery: IDeliveryAddresses
): { payload: IDeliveryPayload } | { errors: { email: boolean; phone: boolean } } => {
    const email = fields.email.trim().toLowerCase();
    const phone = normalizeDeliveryPhone(fields.phone);
    const errors = { email: fields.showEmail && !REGULAR_EXPRESSION_NEW.EMAIL.test(email), phone: fields.showPhone && !phone };

    if (errors.email || errors.phone) return { errors };

    return {
        payload: {
            deliveryEmail: fields.showEmail && email !== delivery.profileEmail ? email : null,
            deliveryPhone: fields.showPhone && phone !== delivery.profilePhone ? phone : null,
        },
    };
};

/** True when an enabled event delivers on this channel, which is when its Send to field shows. */
export const usesChannel = (types: INotificationTypes | null | undefined, channel: 'email' | 'sms'): boolean =>
    Object.values(types || {}).some((type) => type?.enabled === true && type.channels?.[channel] === true);
